// ============================================================================
// KLINISOL CRO - ILO Platform (Enhanced - Professional Minimal Design)
// Clean, conversational interface with user guidance
// Version: 3.1 - Enhanced UX
// Date: November 18, 2025
// Changes: Network icon, "Integrate • Learn • Operate" tagline, Help modal
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TTSButton } from '../components/ILO_TextToSpeech_V2_HUMAN_VOICE';
import { Network, HelpCircle, X, BookOpen, ArrowLeft } from 'lucide-react';

const ILOPlatform = () => {
  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================
  
  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHelp, setShowHelp] = useState(false); // Help modal
  
  // Voice/Audio state
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  
  const conversationEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Auto-scroll to bottom
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations]);

  // ========================================================================
  // AUDIO FUNCTIONS
  // ========================================================================
  
  const playAudio = (url) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = url;
      audioRef.current.play();
      setIsPlaying(true);
      setCurrentAudioUrl(url);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentAudioUrl(null);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentAudioUrl(null);
  };

  // ========================================================================
  // VOICE INPUT
  // ========================================================================
  
  const handleVoiceStart = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processVoiceInput(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Voice input error:', error);
      setError('Microphone access denied. Please enable microphone permissions.');
    }
  };

  const handleVoiceStop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVoiceInput = async (audioBlob) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice-input.webm');
      formData.append('category', 'general');
      
      const response = await fetch(`${API_URL}/voice/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setQuery(data.transcript || data.transcription);
        
        const newEntry = {
          id: data.interaction_id || Date.now(),
          query: data.transcript || data.transcription,
          response: data.response,
          sources: data.sources || [],
          timestamp: new Date().toISOString(),
          metrics: data.metrics || {},
          audioUrl: null
        };
        
        setConversations(prev => [...prev, newEntry]);
        
        // Auto-play OpenAI voice
        if (data.audioResponse) {
          const audioBlob = new Blob(
            [Uint8Array.from(atob(data.audioResponse), c => c.charCodeAt(0))],
            { type: 'audio/mpeg' }
          );
          const audioUrl = URL.createObjectURL(audioBlob);
          playAudio(audioUrl);
        }
      } else {
        setError(data.message || 'Failed to process voice input');
      }
    } catch (error) {
      console.error('Voice processing error:', error);
      setError('Failed to process voice input. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================================================
  // SUBMIT QUERY
  // ========================================================================
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/ilo/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          query: query,
          category: 'general',
          selectedLevel: null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        const newEntry = {
          id: data.interaction_id,
          query: query,
          response: data.response,
          sources: data.sources || [],
          timestamp: new Date().toISOString(),
          metrics: data.metrics || {}
        };
        
        setConversations(prev => [...prev, newEntry]);
        setQuery('');
      } else {
        setError(data.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('Query error:', error);
      setError('Failed to connect to ILO. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Clear conversation history?')) {
      setConversations([]);
      setQuery('');
    }
  };

  // ========================================================================
  // RENDER FUNCTIONS
  // ========================================================================

  const renderInlineSources = (sources) => {
    if (!sources || sources.length === 0) return null;

    return (
      <div className="mt-4 pt-3 border-t border-gray-100">
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-gray-600 hover:text-[#E91E63] flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" />
            <span>Sources ({sources.length})</span>
            <span className="ml-1 text-gray-400 group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="mt-2 space-y-2">
            {sources.map((source, idx) => (
              <div key={idx} className="bg-gray-50 rounded p-2 text-xs">
                <div className="font-medium text-gray-900">{source.title || source.ref}</div>
                {source.excerpt && (
                  <div className="text-gray-600 mt-1 italic">"{source.excerpt}"</div>
                )}
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  };

  const renderConversation = (conv, index) => {
    return (
      <div key={conv.id || index} className="space-y-6 mb-8">
        {/* User Query */}
        <div className="flex justify-end">
          <div className="bg-gradient-to-r from-[#475569] to-[#334155] text-white px-5 py-3 rounded-2xl rounded-tr-sm max-w-2xl shadow-sm">
            {conv.query}
          </div>
        </div>

        {/* ILO Response */}
        <div className="flex gap-4">
          <div className="w-8 h-8 rounded-full bg-[#E91E63] flex items-center justify-center flex-shrink-0">
            <Network className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed">
              {conv.response}
            </div>

            {/* Inline Sources */}
            {renderInlineSources(conv.sources)}

            {/* TTS Button */}
            <div className="mt-4">
              <TTSButton text={conv.response} />
            </div>

            {/* Advanced View (Optional) */}
            {showAdvanced && conv.metrics && (
              <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-600 font-mono">
                <div>Response Time: {conv.metrics.responseTimeMs}ms</div>
                <div>Tokens: {conv.metrics.responseTokens}</div>
                <div>Docs Used: {conv.metrics.knowledgeDocsUsed}</div>
                <div>ID: {conv.id}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========================================================================
  // HELP MODAL
  // ========================================================================

  const HelpModal = () => {
    if (!showHelp) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
            <div className="flex items-center gap-3">
              <Network className="w-6 h-6" />
              <h3 className="font-bold text-xl">ILO User Guide</h3>
            </div>
            <button 
              onClick={() => setShowHelp(false)}
              className="hover:bg-white/20 rounded-lg p-2 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            
            {/* What is ILO */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">What is ILO?</h4>
              <p className="text-gray-700 mb-3">
                ILO stands for <strong className="text-[#E91E63]">Integrate • Learn • Operate</strong> - 
                your AI-powered clinical research assistant.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="font-bold text-blue-900 mb-1">🔗 Integrate</div>
                  <div className="text-xs text-blue-800">Access all SOPs, regulations, and training materials in one place</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="font-bold text-green-900 mb-1">🎓 Learn</div>
                  <div className="text-xs text-green-800">Ask questions and get instant answers with citations</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="font-bold text-purple-900 mb-1">⚡ Operate</div>
                  <div className="text-xs text-purple-800">Get step-by-step operational guidance</div>
                </div>
              </div>
            </section>

            {/* How to Use */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">How to Use ILO</h4>
              <ol className="space-y-2 text-gray-700">
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">1.</span>
                  <span>Type your question in plain English in the input box</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">2.</span>
                  <span>Press <kbd className="px-2 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Enter</kbd> or click the arrow button</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">3.</span>
                  <span>ILO provides an answer with relevant citations</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">4.</span>
                  <span>Click "Sources" to view full documents</span>
                </li>
              </ol>
            </section>

            {/* Example Queries */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">Example Queries</h4>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setQuery('What is GCP?');
                    setShowHelp(false);
                  }}
                  className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#E91E63] hover:bg-[#E91E63]/5 transition-all"
                >
                  <div className="font-medium text-gray-900">What is GCP?</div>
                  <div className="text-xs text-gray-500 mt-1">Get definitions and explanations</div>
                </button>
                
                <button
                  onClick={() => {
                    setQuery('How do I report an adverse event?');
                    setShowHelp(false);
                  }}
                  className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#E91E63] hover:bg-[#E91E63]/5 transition-all"
                >
                  <div className="font-medium text-gray-900">How do I report an adverse event?</div>
                  <div className="text-xs text-gray-500 mt-1">Step-by-step procedures</div>
                </button>

                <button
                  onClick={() => {
                    setQuery('Show me document control SOP');
                    setShowHelp(false);
                  }}
                  className="w-full text-left px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#E91E63] hover:bg-[#E91E63]/5 transition-all"
                >
                  <div className="font-medium text-gray-900">Show me document control SOP</div>
                  <div className="text-xs text-gray-500 mt-1">Access SOPs and regulations</div>
                </button>
              </div>
            </section>

            {/* Tips */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">💡 Pro Tips</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Be specific: "What is the timeline for SAE reporting?" vs. "Tell me about safety"</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Use action words: "How do I...", "Show me...", "Explain..."</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Always check sources for full context</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Use voice input (🎤) for hands-free operation</span>
                </li>
              </ul>
            </section>

            {/* FDA Compliance */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                <span>🛡️</span>
                <span>Designed for FDA 21 CFR Part 11</span>
              </h4>
              <p className="text-sm text-blue-800">
                All interactions are logged with audit trails. System designed with 21 CFR Part 11 
                principles. Formal validation pending. Never share patient identifiers or confidential 
                trial data through ILO.
              </p>
            </section>

          </div>
        </div>
      </div>
    );
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white px-6 py-4 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back to Dashboard Button */}
            <button
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm transition-all"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-white" strokeWidth={2.5} />
            </button>

            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <Network className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-xl font-bold">
                KLIN<span className="text-white/90">i</span>SOL ILO
              </h1>
              <p className="text-xs text-white/80">Integrate • Learn • Operate</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Help Button */}
            <button
              onClick={() => setShowHelp(true)}
              className="px-3 py-1.5 text-xs text-white/90 hover:text-white border border-white/30 rounded hover:bg-white/10 transition-all flex items-center gap-1.5"
              title="ILO User Guide"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Help</span>
            </button>

            {/* Advanced View Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="px-3 py-1 text-xs text-white/90 hover:text-white border border-white/30 rounded hover:bg-white/10 transition-all"
              title="Toggle advanced metrics"
            >
              {showAdvanced ? '👁️ Hide' : '⚙️ Details'}
            </button>
            
            {/* Clear Button */}
            {conversations.length > 0 && (
              <button
                onClick={handleClear}
                className="px-3 py-1 text-xs text-white/90 hover:text-white hover:bg-white/10 rounded transition-all"
              >
                🗑️ Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {conversations.length === 0 ? (
            // WELCOME SCREEN
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#475569] to-[#E91E63] rounded-2xl mb-6 shadow-lg">
                <Network className="w-12 h-12 text-white" strokeWidth={2} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome to ILO
              </h2>
              <p className="text-gray-600 text-lg mb-1">
                Your AI assistant for clinical research operations
              </p>
              <p className="text-sm text-[#E91E63] font-semibold mb-12">
                Integrate • Learn • Operate
              </p>
              
              <div className="space-y-3 max-w-2xl mx-auto">
                <button 
                  className="w-full px-6 py-4 bg-white border border-gray-200 rounded-lg text-left hover:border-[#E91E63] hover:shadow-md transition-all group"
                  onClick={() => setQuery('What is GCP?')}
                >
                  <div className="font-medium text-gray-900 group-hover:text-[#E91E63]">What is GCP?</div>
                  <div className="text-sm text-gray-500 mt-1">Learn about Good Clinical Practice guidelines</div>
                </button>
                
                <button 
                  className="w-full px-6 py-4 bg-white border border-gray-200 rounded-lg text-left hover:border-[#E91E63] hover:shadow-md transition-all group"
                  onClick={() => setQuery('How do I report an adverse event?')}
                >
                  <div className="font-medium text-gray-900 group-hover:text-[#E91E63]">How do I report an adverse event?</div>
                  <div className="text-sm text-gray-500 mt-1">Get step-by-step safety reporting guidance</div>
                </button>
                
                <button 
                  className="w-full px-6 py-4 bg-white border border-gray-200 rounded-lg text-left hover:border-[#E91E63] hover:shadow-md transition-all group"
                  onClick={() => setQuery('Show me all SOPs')}
                >
                  <div className="font-medium text-gray-900 group-hover:text-[#E91E63]">Show me all SOPs</div>
                  <div className="text-sm text-gray-500 mt-1">Browse standard operating procedures</div>
                </button>
              </div>
            </div>
          ) : (
            // CONVERSATION AREA
            <>
              {conversations.map((conv, index) => renderConversation(conv, index))}
              <div ref={conversationEndRef} />
            </>
          )}

          {/* LOADING */}
          {isLoading && (
            <div className="flex gap-4 mb-8">
              <div className="w-8 h-8 rounded-full bg-[#E91E63] flex items-center justify-center flex-shrink-0">
                <Network className="w-4 h-4 text-white animate-pulse" strokeWidth={2.5} />
              </div>
              <div className="flex-1 pt-2">
                <div className="flex items-center gap-2 text-[#475569]">
                  <div className="w-2 h-2 bg-[#E91E63] rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-[#E91E63] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-[#E91E63] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  <span className="ml-2 text-sm">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          {/* ERROR */}
          {error && (
            <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-800 mb-4">
              <span>⚠️</span>
              <span className="flex-1 text-sm">{error}</span>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">✕</button>
            </div>
          )}

          {/* AUDIO PLAYING */}
          {isPlaying && (
            <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
              <span>🔊</span>
              <span className="flex-1 text-sm text-blue-900">ILO is speaking...</span>
              <button 
                onClick={stopAudio}
                className="px-3 py-1 bg-red-500 text-white rounded text-sm font-medium hover:bg-red-600"
              >
                Stop
              </button>
            </div>
          )}
        </div>
      </div>

      {/* INPUT AREA (FIXED BOTTOM) */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 shadow-lg">
        <div className="max-w-4xl mx-auto">
          {/* Keyboard Hint */}
          <div className="flex items-center justify-between mb-2 text-xs text-gray-400">
            <span>💬 Press <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono">Enter</kbd> to send • <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono">Shift+Enter</kbd> for new line</span>
            <span>🎤 Voice available</span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="flex gap-3">
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about SOPs, regulations, procedures..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-[#E91E63] focus:ring-2 focus:ring-[#E91E63]/20 disabled:bg-gray-100"
                rows={3}
                disabled={isLoading || isRecording}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={isRecording ? handleVoiceStop : handleVoiceStart}
                  className={`px-4 py-3 rounded-lg font-medium transition-all ${
                    isRecording 
                      ? 'bg-red-500 text-white animate-pulse' 
                      : 'bg-[#475569] text-white hover:bg-[#334155]'
                  }`}
                  disabled={isLoading}
                  title={isRecording ? "Stop recording" : "Voice input"}
                >
                  {isRecording ? '⏹️' : '🎤'}
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-3 bg-gradient-to-r from-[#E91E63] to-[#C2185B] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
                  disabled={isLoading || !query.trim() || isRecording}
                  title="Send message"
                >
                  ➜
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* HELP MODAL */}
      <HelpModal />

      {/* HIDDEN AUDIO PLAYER */}
      <audio ref={audioRef} onEnded={handleAudioEnded} className="hidden" />
    </div>
  );
};

export default ILOPlatform;
